import { H1, Lead, Paragraph, RadioGroup, Display } from '@sbab-ui/components'
import React, { useEffect, useState } from 'react'
import { BooliResponse, Suggestion } from '../../api/httpHandlers'
import { AppAction, UserData } from '../../appState'
import lightbulb from '../../assets/lightbulb.svg'
import BackArrow from '../../icons/BackArrow'
import OverlayHeader from '../AppHeader'
import InterestRateApp from './InterestRateApp/InterestRateApp'
import SavingsEstimation from './SavingsEstimation'
import SuggestionItem from './SuggestionItem'
import EnergyRenovation from './EnergyRenovationBanner'
import EnergyConsultingBanner from './EnergyConsultingBanner'
import ReadMore from './ReadMore'

interface Props {
  resultData?: BooliResponse
  userData: UserData
  appDispatch: React.Dispatch<AppAction>
}

export default function Result({ resultData, userData, appDispatch }: Readonly<Props>) {
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>('')

  useEffect(() => {}, [selectedSuggestion])

  function getSelectedSuggestion() {
    return resultData?.suggestions.find((suggestion) => suggestion.id === selectedSuggestion) ?? ''
  }

  function processSuggestions(suggestions: Suggestion[], solarPanelPower: boolean) {
    const unwantedSuggestions = [
      'wood-chips',
      'pellets',
      'ftx',
      '3_glass_u',
    ]
    const filteredSuggestions = suggestions.filter((suggestion) => {
      return !unwantedSuggestions.includes(suggestion.id)
    })
    if (!solarPanelPower) return filteredSuggestions
    return filteredSuggestions.filter((suggestion) => suggestion.key !== 'solarpanel_power')
  }

  const processedSuggestions = processSuggestions(
    resultData?.suggestions ?? [],
    userData.solar_panel_power === 'true',
  )

  return (
    <div data-testid={`result`}>
      <OverlayHeader />
      <div className="mx-2 my-6 flex-col gap-4 sm:mx-20 sm:my-10 sm:gap-20 lg:mx-64">
        <button className="mb-6 flex items-center" onClick={() => appDispatch({ type: 'goBack' })}>
          <BackArrow />
          <Lead className="!m-0">Tillbaka</Lead>
        </button>

        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex flex-col sm:gap-4 lg:mt-7 lg:w-[750px]">
            <H1 className="!mb-4 text-black md:!mb-0">
              Härligt! Då har vi några förslag på hur du kan spara energi
            </H1>

            <Display as="p" id={"label-:rf:"} className="!m-0 !mb-5 !text-2xl !font-thin !text-black">
              Välj en åtgärd nedan och se effekten på din förbrukning.
            </Display>
          </div>
          <div className="-mt-64 hidden justify-end 2xl:mr-4 2xl:flex">
            <img src={lightbulb} alt="ikon" aria-label="Ikon Glödlampa" />
          </div>
          <div className="flex flex-col items-start gap-8 md:flex-row ">
            <div className="flex w-full flex-col gap-8 md:w-1/2">
              <RadioGroup
                className="w-full"
                name="group-options"
                onChange={(e) => setSelectedSuggestion(e)}
                value={selectedSuggestion}
              >
                {processedSuggestions.map((suggestion) => (
                  <SuggestionItem
                    key={suggestion.id}
                    suggestion={suggestion}
                    setSelectedSuggestion={setSelectedSuggestion}
                    selected={suggestion.id === selectedSuggestion}
                  />
                ))}
              </RadioGroup>
              <Paragraph className="!-mt-10 !mb-[24px] w-full">
                <span className="font-bold">Bra att veta:</span> Från juli 2023 finns det bidrag att
                söka för dig som vill energieffektivisera ditt hus (gäller ej solceller).{' '}
                <a href="https://www.boverket.se/" className="!text-semantic-link underline">
                  Läs mer om vad som gäller hos Boverket.
                </a>
              </Paragraph>
            </div>
            <SavingsEstimation
              suggestion={getSelectedSuggestion()}
              totalConsumption={resultData?.total_consumption}
            />
          </div>
          <InterestRateApp />
          <EnergyRenovation />
          <EnergyConsultingBanner />

          <ReadMore />
        </div>
      </div>
    </div>
  )
}
